import { useId } from 'react';
import { BOT_LEVELS, describeLevel } from '../../../games/analysis/bot.ts';
import type { BotLevel } from '../../../games/analysis/bot.ts';
import type { Seat } from '../../../shared/domain.ts';
import { verdictStyle } from '../review/reviewText.ts';
import { themeVariables } from '../match/presentation.ts';
import { Board } from '../match/Board.tsx';
import type { BoardAnnotation } from '../match/Board.tsx';
import { columnName, verdictHeadline, worthRetrying } from './coachText.ts';
import { usePractice } from './usePractice.ts';
import type { PracticeView } from './usePractice.ts';

export interface CoachPageProps {
  readonly spec: PracticeView['spec'];
  readonly onExit: () => void;
  /** Runs the engine. Defaults to a real worker; tests pass their own. */
  readonly createWorker?: () => Worker;
}

/**
 * The verdict chip, drawn from the same classes the review screen uses.
 *
 * Colour is never the only channel: one short bar for a good move, four tall
 * heavy ones for a blunder, so the severity survives greyscale, forced colours
 * and a compressed stream (WCAG 1.4.1).
 */
function VerdictChip({ verdict }: { verdict: PracticeView['lastReview'] }) {
  if (verdict === null) return null;
  const style = verdictStyle(verdict.verdict);
  return (
    <p className={`verdict verdict--${verdict.verdict}`}>
      <span className="verdict__tick" aria-hidden="true">
        {Array.from({ length: style.severity + 1 }, (_unused, bar) => (
          <span key={bar} className="verdict__bar" />
        ))}
      </span>
      {verdictHeadline(verdict.verdict)}
    </p>
  );
}

/** The seat the player is not. Practice is always two-handed. */
function otherSeat(seat: Seat): Seat {
  return seat === 1 ? 2 : 1;
}

/** Whose turn it is, in the words a player would use. */
function statusLine(view: PracticeView): string {
  if (view.over) {
    switch (view.result) {
      case 'win':
        return 'You win.';
      case 'loss':
        return 'The coach wins this one.';
      default:
        return 'A draw. The board is full.';
    }
  }
  if (view.thinking) return 'The coach is thinking\u2026';
  return 'Your turn.';
}

/**
 * Play the engine, and be taught while you do it.
 *
 * The screen is built around one pedagogical decision: **point before telling**.
 * When something decisive is on the board the coach says that it is there and
 * stops. The answer sits behind a button and is drawn as a ghost disc on the
 * real board rather than described as a column number, because advice that
 * arrives as prose has to be translated back into a place before it is any use.
 *
 * Nothing here touches the socket. A practice game has no match id and no
 * rating, which is exactly what makes taking a move back honest.
 */
export function CoachPage({ spec, onExit, createWorker }: CoachPageProps) {
  const { view, drop, reveal, takeBack, newGame, setLevel } = usePractice({
    spec,
    ...(createWorker ? { createWorker } : {}),
  });
  const levelId = useId();

  const { line } = view;
  const showAnswer = view.revealed && line.answer !== null;

  /*
   * The coach's mark on the board, which must never contradict the words.
   *
   * `intent` decides both whose disc is drawn and whether anything is drawn at
   * all. An earlier version always ghosted a disc of your own colour into the
   * first column the line named — including the trap columns, whose whole point
   * is that you must not play there. Following the marker lost games it had
   * just warned about.
   *
   * Only ever the first column named: a ghost in every one stops being a
   * recommendation and becomes a second board to read.
   */
  const marked = line.params[0];
  const suggestion: BoardAnnotation | null =
    !showAnswer || marked === undefined || line.intent === 'none'
      ? null
      : line.intent === 'threat'
        ? {
            column: marked,
            seat: otherSeat(view.yourSeat),
            label: `they complete a line at column ${columnName(marked)}`,
          }
        : {
            column: marked,
            seat: view.yourSeat,
            label: `the coach suggests column ${columnName(marked)}`,
          };

  return (
    <section className="coach" aria-labelledby="coach-heading">
      <header className="coach__head">
        <h2 id="coach-heading" className="coach__heading">
          Practice with the coach
        </h2>
        <p className="coach__lede">
          Nothing here is rated or recorded, so try the move you are unsure about.
        </p>
      </header>

      <div className="coach__body">
        <div className="coach__boardCol">
          <Board
            spec={spec}
            board={view.board}
            yourSeat={view.yourSeat}
            legalColumns={view.yourTurn ? view.legalColumns : []}
            pendingColumn={null}
            lastMoveIndex={view.lastMoveCell}
            winningLine={view.winningLine}
            interactive={view.yourTurn}
            annotation={suggestion}
            onDrop={drop}
          />

          {/*
            Which disc is yours.

            A live match names both players in the status bar; practice has no
            names to show, so without this the first thing a new player has to
            work out is which colour they are — by dropping one and watching.
            The seat colours come from the spec's own theme, so this cannot
            drift from the board beside it.
          */}
          <p className="coach__legend-seats" style={themeVariables(spec)}>
            <span className="coach__seat">
              <span
                className={`coach__seatDot coach__seatDot--seat${String(view.yourSeat)}`}
                aria-hidden="true"
              />
              You
            </span>
            <span className="coach__seat">
              <span
                className={`coach__seatDot coach__seatDot--seat${String(otherSeat(view.yourSeat))}`}
                aria-hidden="true"
              />
              The coach
            </span>
          </p>
        </div>

        <aside className="coach__rail">
          {/*
            One live region for everything the coach says. Polite, because a
            player mid-move must not be interrupted, and a single region means a
            screen reader hears one coherent update per turn rather than three
            racing ones.
          */}
          <div className="coach__panel" aria-live="polite">
            <p className="coach__status">{statusLine(view)}</p>

            {!view.over && (
              <>
                <p className={`coach__line coach__line--${line.tone}`}>{line.nudge}</p>

                {line.answer !== null &&
                  (showAnswer ? (
                    <p className="coach__answer">{line.answer}</p>
                  ) : (
                    <button type="button" className="button button--ghost" onClick={reveal}>
                      Show me
                    </button>
                  ))}
              </>
            )}

            {view.lastReview !== null && (
              <div className="coach__verdict">
                <VerdictChip verdict={view.lastReview} />
                {view.lastReview.bestParam !== view.lastReview.param && (
                  <p className="coach__better">
                    Column {columnName(view.lastReview.bestParam)} was stronger.
                  </p>
                )}
                {worthRetrying(view.lastReview.verdict) && view.canTakeBack && (
                  <button type="button" className="button button--ghost" onClick={takeBack}>
                    Take it back
                  </button>
                )}
              </div>
            )}

            {view.error !== null && (
              <p className="coach__error" role="alert">
                The coach stopped: {view.error}. Start a new game to carry on.
              </p>
            )}
          </div>

          <div className="coach__controls">
            <fieldset className="coach__levels">
              <legend className="coach__legend" id={levelId}>
                How hard it tries
              </legend>
              {BOT_LEVELS.map((level: BotLevel) => (
                <label className="coach__level" key={level}>
                  <input
                    type="radio"
                    name={`${levelId}-level`}
                    value={level}
                    checked={view.level === level}
                    onChange={() => setLevel(level)}
                  />
                  <span className="coach__levelName">{level}</span>
                  <span className="coach__levelWhat">{describeLevel(level)}</span>
                </label>
              ))}
            </fieldset>

            <div className="coach__actions">
              <button
                type="button"
                className="button button--ghost"
                onClick={takeBack}
                disabled={!view.canTakeBack}
              >
                Take back a move
              </button>
              <button
                type="button"
                className="button button--primary"
                onClick={() => newGame(view.level, true)}
              >
                New game
              </button>
              <button
                type="button"
                className="button button--quiet"
                onClick={() => newGame(view.level, false)}
              >
                Let it start
              </button>
              <button type="button" className="button button--quiet" onClick={onExit}>
                Done
              </button>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
